import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { CircleDot, Save, Loader2, Plus, Trash2, Check, X, Users, Search, Download, Settings2, BarChart3, UserCheck, Euro, Calendar, Gift, ExternalLink, Dices, Sparkles, Box, ArrowLeft, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const GAME_TYPES = [
  { id: 'roue', label: 'La Roue', icon: CircleDot, desc: 'Roue de la fortune animee', color: '#C9A84C' },
  { id: 'grattage', label: 'Grattage', icon: Sparkles, desc: 'Carte a gratter tactile', color: '#009893' },
  { id: 'coffre', label: 'Coffre Mystere', icon: Box, desc: '3 coffres, 1 surprise', color: '#C0604A' },
];

const RoueGestion = () => {
  const [tab, setTab] = useState('jeu');
  const [prizes, setPrizes] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [newPrize, setNewPrize] = useState({ id: '', label: '', emoji: '', weight: 10, win: true, active: true, cout: 0 });
  const [clientDetail, setClientDetail] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statsRes] = await Promise.all([api.get('/roue/config'), api.get('/roue/stats')]);
      setPrizes(configRes.data.prizes || []);
      setSettings(configRes.data.settings || {});
      setStats(statsRes.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const loadClients = useCallback(async () => {
    try { const res = await api.get(`/roue/clients?search=${search}`); setClients(res.data); } catch {}
  }, [search]);

  useEffect(() => { if (tab === 'clients') loadClients(); }, [tab, loadClients]);

  const savePrizes = async () => {
    setSaving(true);
    try { await api.put('/roue/config/prizes', { prizes }); setDirty(false); toast.success('Lots sauvegardes'); }
    catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const saveSettings = async (s) => {
    try { await api.put('/roue/config/settings', s); setSettings(s); toast.success('Sauvegarde'); }
    catch { toast.error('Erreur'); }
  };

  const updatePrize = (idx, field, value) => {
    setPrizes(p => p.map((pr, i) => i === idx ? { ...pr, [field]: value } : pr));
    setDirty(true);
  };
  const deletePrize = (idx) => { setPrizes(p => p.filter((_, i) => i !== idx)); setDirty(true); };
  const addPrize = () => {
    if (!newPrize.label.trim() || !newPrize.id.trim()) return;
    setPrizes(p => [...p, { ...newPrize, id: newPrize.id.toUpperCase() }]);
    setNewPrize({ id: '', label: '', emoji: '', weight: 10, win: true, active: true, cout: 0 });
    setAdding(false); setDirty(true);
  };

  const openClient = (c) => {
    setClientDetail(c);
    setEditForm({ prenom: c.prenom || '', nom: c.nom || '', email: c.email || '', telephone: c.telephone || '', adresse: c.adresse || '', date_naissance: c.date_naissance || '' });
    setEditing(false);
    setConfirmDelete(false);
  };
  const closeClient = () => { setClientDetail(null); setEditing(false); setConfirmDelete(false); };
  const saveClient = async () => {
    if (!clientDetail) return;
    setSavingClient(true);
    try {
      const res = await api.put(`/roue/clients/${clientDetail.id}`, editForm);
      setClientDetail({ ...clientDetail, ...res.data });
      setEditing(false);
      toast.success('Client modifie');
      loadClients();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    setSavingClient(false);
  };
  const deleteClient = async () => {
    if (!clientDetail) return;
    try {
      await api.delete(`/roue/clients/${clientDetail.id}`);
      toast.success('Client supprime');
      closeClient();
      loadClients();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const totalWeight = prizes.filter(p => p.active).reduce((s, p) => s + (p.weight || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="roue-gestion">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Dices size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Jeux & Fidelite</h1>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button size="sm" onClick={savePrizes} disabled={saving}
              className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="save-prizes">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          )}
          <a href="/roue" target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors">
            <ExternalLink size={12} /> Tester
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-[rgb(var(--lm-surface))]" data-testid="roue-tabs">
        {[
          { id: 'jeu', label: 'Jeu actif', icon: Dices },
          { id: 'lots', label: 'Lots', icon: Gift },
          { id: 'stats', label: 'Stats', icon: BarChart3 },
          { id: 'clients', label: 'Clients', icon: Users },
          { id: 'reglages', label: 'Reglages', icon: Settings2 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 rounded-md text-[11px] font-medium flex items-center justify-center gap-1 transition-all ${
              tab === t.id ? 'bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'
            }`} data-testid={`roue-tab-${t.id}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: JEU ACTIF ═══ */}
      {tab === 'jeu' && (
        <div className="space-y-4">
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Choisissez le jeu affiche aux clients :</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {GAME_TYPES.map(g => {
              const active = (settings.game_type || 'roue') === g.id;
              return (
                <button key={g.id} onClick={() => saveSettings({ ...settings, game_type: g.id })}
                  className={`lm-card p-5 text-left transition-all relative overflow-hidden ${
                    active ? 'ring-2 ring-[rgb(var(--lm-gold))]' : 'hover:border-[rgb(var(--lm-gold)/0.3)]'
                  }`} data-testid={`game-select-${g.id}`}>
                  {active && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[rgb(var(--lm-gold))] flex items-center justify-center">
                      <Check size={12} className="text-[rgb(var(--lm-bg))]" />
                    </div>
                  )}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${g.color}20` }}>
                    <g.icon size={20} style={{ color: g.color }} />
                  </div>
                  <h3 className="text-sm font-semibold text-[rgb(var(--lm-text))] mb-1">{g.label}</h3>
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{g.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Quick stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <MiniStat label="Clients" value={stats.total_clients} icon={Users} />
              <MiniStat label="Bons emis" value={stats.total_vouchers} icon={Gift} />
              <MiniStat label="Utilises" value={`${stats.taux_utilisation}%`} icon={UserCheck} />
              <MiniStat label="Cout ce mois" value={formatCurrency(stats.cout_mensuel)} icon={Euro} />
            </div>
          )}

          {/* Active game preview */}
          <div className="lm-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Apercu du jeu actif</h2>
              <span className="text-xs text-[rgb(var(--lm-gold))] font-medium">
                {GAME_TYPES.find(g => g.id === (settings.game_type || 'roue'))?.label}
              </span>
            </div>
            <div className="bg-[rgb(var(--lm-bg))] rounded-xl p-6 flex items-center justify-center min-h-[120px]">
              {(settings.game_type || 'roue') === 'roue' && <PreviewRoue />}
              {settings.game_type === 'grattage' && <PreviewGrattage />}
              {settings.game_type === 'coffre' && <PreviewCoffre />}
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: LOTS ═══ */}
      {tab === 'lots' && (
        <div className="space-y-3">
          <div className="lm-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Lots</h2>
              <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">Poids total: {totalWeight}</span>
            </div>
            <div className="space-y-1.5">
              {prizes.map((p, idx) => {
                const prob = totalWeight > 0 ? ((p.weight || 0) / totalWeight * 100).toFixed(1) : '0';
                return (
                  <div key={idx} className={`rounded-lg p-3 transition-all ${p.active ? 'bg-[rgb(var(--lm-surface-elevated)/0.3)]' : 'opacity-40 bg-[rgb(var(--lm-surface-elevated)/0.1)]'}`}
                    data-testid={`prize-row-${idx}`}>
                    <div className="flex items-center gap-3">
                      <Switch checked={p.active} onCheckedChange={v => updatePrize(idx, 'active', v)} className="scale-75" data-testid={`prize-active-${idx}`} />
                      <span className="text-lg w-8 text-center">{p.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[rgb(var(--lm-text))] font-medium truncate">{p.label}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${p.win ? 'bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-red)/0.1)] text-[rgb(var(--lm-red))]'}`}>
                            {p.win ? 'Gagnant' : 'Perdu'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Poids: {p.weight}</span>
                          <span className="text-[10px] text-[rgb(var(--lm-gold))]">Prob: {prob}%</span>
                          {p.win && <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Cout: {formatCurrency(p.cout || 0)}</span>}
                        </div>
                      </div>
                      <button onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                        className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors text-xs px-2 py-1 rounded hover:bg-[rgb(var(--lm-surface-elevated))]"
                        data-testid={`prize-edit-${idx}`}>
                        Modifier
                      </button>
                      <button onClick={() => deletePrize(idx)}
                        className="text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-colors" data-testid={`prize-delete-${idx}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {editingIdx === idx && (
                      <div className="mt-3 pt-3 border-t border-[rgb(var(--lm-border))] grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                          <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Emoji</label>
                          <Input value={p.emoji} onChange={e => updatePrize(idx, 'emoji', e.target.value)}
                            className="h-7 text-sm text-center bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={`prize-emoji-${idx}`} />
                        </div>
                        <div>
                          <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Nom du lot</label>
                          <Input value={p.label} onChange={e => updatePrize(idx, 'label', e.target.value)}
                            className="h-7 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={`prize-label-${idx}`} />
                        </div>
                        <div>
                          <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Poids</label>
                          <Input type="number" value={p.weight} onChange={e => updatePrize(idx, 'weight', parseInt(e.target.value) || 0)}
                            className="h-7 text-xs font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={`prize-weight-${idx}`} />
                        </div>
                        <div>
                          <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Cout (EUR)</label>
                          <Input type="number" step="0.5" value={p.cout} onChange={e => updatePrize(idx, 'cout', parseFloat(e.target.value) || 0)}
                            className="h-7 text-xs font-mono bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid={`prize-cout-${idx}`} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add new */}
            {adding ? (
              <div className="mt-3 p-4 rounded-lg bg-[rgb(var(--lm-surface-elevated))] space-y-3">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))] font-medium">Nouveau lot</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Input value={newPrize.id} onChange={e => setNewPrize(p => ({ ...p, id: e.target.value }))} placeholder="ID (ex: APERO)"
                    className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono uppercase" data-testid="new-prize-id" />
                  <Input value={newPrize.label} onChange={e => setNewPrize(p => ({ ...p, label: e.target.value }))} placeholder="Nom du lot"
                    className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" autoFocus data-testid="new-prize-label" />
                  <Input value={newPrize.emoji} onChange={e => setNewPrize(p => ({ ...p, emoji: e.target.value }))} placeholder="Emoji"
                    className="h-8 text-xs text-center bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="new-prize-emoji" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Poids</label>
                    <Input type="number" value={newPrize.weight} onChange={e => setNewPrize(p => ({ ...p, weight: parseInt(e.target.value) || 0 }))}
                      className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="new-prize-weight" />
                  </div>
                  <div>
                    <label className="text-[9px] text-[rgb(var(--lm-text-muted))]">Cout (EUR)</label>
                    <Input type="number" step="0.5" value={newPrize.cout} onChange={e => setNewPrize(p => ({ ...p, cout: parseFloat(e.target.value) || 0 }))}
                      className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="new-prize-cout" />
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] mb-2">
                      <input type="checkbox" checked={newPrize.win} onChange={e => setNewPrize(p => ({ ...p, win: e.target.checked }))} /> Gagnant
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setAdding(false)} className="text-[rgb(var(--lm-text-muted))] text-xs">Annuler</button>
                  <Button size="sm" onClick={addPrize} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-7 text-xs" data-testid="confirm-add-prize">
                    <Check size={12} className="mr-1" /> Ajouter
                  </Button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)}
                className="mt-3 flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors" data-testid="add-prize-btn">
                <Plus size={12} /> Ajouter un lot
              </button>
            )}
          </div>

          {/* Cost estimate */}
          <div className="lm-card p-4">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Estimation mensuelle (100 parties)</h2>
            <div className="space-y-1">
              {prizes.filter(p => p.active && p.win).map((p, i) => {
                const prob = totalWeight > 0 ? (p.weight / totalWeight) : 0;
                const nb = Math.round(prob * 100);
                return (
                  <div key={i} className="flex items-center justify-between text-xs py-1">
                    <span className="text-[rgb(var(--lm-text-secondary))]">{p.emoji} {p.label}</span>
                    <span className="font-mono text-[rgb(var(--lm-gold))]">~{nb}x = {formatCurrency(nb * (p.cout || 0))}</span>
                  </div>
                );
              })}
              <div className="border-t border-[rgb(var(--lm-border))] pt-2 mt-1 flex justify-between">
                <span className="text-sm font-semibold text-[rgb(var(--lm-gold))]">Total estime</span>
                <span className="font-mono font-bold text-[rgb(var(--lm-gold))]" data-testid="cout-estime">
                  {formatCurrency(prizes.filter(p => p.active && p.win).reduce((s, p) => {
                    const prob = totalWeight > 0 ? p.weight / totalWeight : 0;
                    return s + Math.round(prob * 100) * (p.cout || 0);
                  }, 0))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: STATS ═══ */}
      {tab === 'stats' && stats && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Clients" value={stats.total_clients} color="gold" testId="stat-clients" />
            <StatCard icon={Gift} label="Bons emis" value={stats.total_vouchers} color="text" testId="stat-vouchers" />
            <StatCard icon={UserCheck} label="Utilisation" value={`${stats.taux_utilisation}%`} color="green" testId="stat-taux" />
            <StatCard icon={Euro} label="Bons ce mois" value={stats.nb_bons_mois} color="orange" testId="stat-mois" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="lm-card p-4 text-center">
              <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] mb-1">Cout total (emis ce mois)</p>
              <p className="font-mono text-2xl text-[rgb(var(--lm-gold))]" data-testid="cout-total">{formatCurrency(stats.cout_mensuel)}</p>
            </div>
            <div className="lm-card p-4 text-center">
              <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] mb-1">Cout reel (utilises)</p>
              <p className="font-mono text-2xl text-[rgb(var(--lm-red))]" data-testid="cout-utilise">{formatCurrency(stats.cout_utilise)}</p>
            </div>
          </div>
          {stats.prize_distribution?.length > 0 && (
            <div className="lm-card p-4">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Distribution</h2>
              <div className="space-y-2">
                {stats.prize_distribution.map(p => {
                  const mx = Math.max(...stats.prize_distribution.map(x => x.count), 1);
                  return (
                    <div key={p.prize_id} className="flex items-center gap-3">
                      <span className="text-sm w-5">{p.emoji}</span>
                      <span className="text-[rgb(var(--lm-text-secondary))] text-xs w-28 truncate">{p.label}</span>
                      <div className="flex-1 bg-[rgb(var(--lm-border))] rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-[rgb(var(--lm-gold))] rounded-full" style={{ width: `${(p.count / mx) * 100}%` }} />
                      </div>
                      <span className="text-[rgb(var(--lm-gold))] text-xs font-mono w-6 text-right">{p.count}</span>
                      <span className="text-[rgb(var(--lm-text-muted))] text-xs font-mono w-14 text-right">{formatCurrency(p.count * (p.cout || 0))}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {stats.recent?.length > 0 && (
            <div className="lm-card p-4">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Derniers bons</h2>
              <div className="space-y-1">
                {stats.recent.map(v => (
                  <div key={v.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-[rgb(var(--lm-surface-elevated)/0.5)]">
                    <span className="text-[rgb(var(--lm-text-secondary))]">{v.prize_emoji} {v.client_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[rgb(var(--lm-text-muted))]">{v.code}</span>
                      <span className={`w-2 h-2 rounded-full ${v.used ? 'bg-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-gold))]'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB: CLIENTS ═══ */}
      {tab === 'clients' && (
        <div className="space-y-3">
          {clientDetail ? (
            <div className="space-y-3">
              <button onClick={closeClient}
                className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-colors"
                data-testid="back-to-clients">
                <ArrowLeft size={12} /> Retour a la liste
              </button>

              <div className="lm-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-[rgb(var(--lm-text))]">{clientDetail.prenom} {clientDetail.nom}</h2>
                    <p className="text-xs text-[rgb(var(--lm-text-muted))]">{clientDetail.email}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-mono text-[rgb(var(--lm-gold))]">{clientDetail.nb_vouchers || 0} bons</span>
                    <span className="text-[10px] text-[rgb(var(--lm-green))] ml-1">({clientDetail.nb_used || 0} util.)</span>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">depuis {clientDetail.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
              </div>

              <div className="lm-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Informations</h2>
                  {!editing && (
                    <button onClick={() => setEditing(true)}
                      className="flex items-center gap-1 text-xs text-[rgb(var(--lm-gold))] hover:underline"
                      data-testid="edit-client-btn">
                      <Pencil size={11} /> Modifier
                    </button>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Prenom</label>
                        <Input value={editForm.prenom} onChange={e => setEditForm(f => ({ ...f, prenom: e.target.value }))}
                          className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-prenom" />
                      </div>
                      <div>
                        <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Nom</label>
                        <Input value={editForm.nom} onChange={e => setEditForm(f => ({ ...f, nom: e.target.value }))}
                          className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-nom" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Email</label>
                      <Input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Telephone</label>
                        <Input value={editForm.telephone} onChange={e => setEditForm(f => ({ ...f, telephone: e.target.value }))}
                          className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-telephone" />
                      </div>
                      <div>
                        <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Date naissance</label>
                        <Input type="date" value={editForm.date_naissance} onChange={e => setEditForm(f => ({ ...f, date_naissance: e.target.value }))}
                          className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-dob" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Adresse</label>
                      <Input value={editForm.adresse} onChange={e => setEditForm(f => ({ ...f, adresse: e.target.value }))}
                        className="h-8 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-adresse" />
                    </div>
                    <div className="flex gap-2 justify-end pt-1">
                      <button onClick={() => { setEditing(false); setEditForm({ prenom: clientDetail.prenom || '', nom: clientDetail.nom || '', email: clientDetail.email || '', telephone: clientDetail.telephone || '', adresse: clientDetail.adresse || '', date_naissance: clientDetail.date_naissance || '' }); }}
                        className="text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="cancel-edit">Annuler</button>
                      <Button size="sm" onClick={saveClient} disabled={savingClient}
                        className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-7 text-xs" data-testid="save-client">
                        {savingClient ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
                        Sauvegarder
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[
                      { label: 'Telephone', value: clientDetail.telephone || '\u2014' },
                      { label: 'Adresse', value: clientDetail.adresse || '\u2014' },
                      { label: 'Date de naissance', value: clientDetail.date_naissance || '\u2014' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-[rgb(var(--lm-border)/0.3)] last:border-0">
                        <span className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">{item.label}</span>
                        <span className="text-xs text-[rgb(var(--lm-text))]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="lm-card p-4">
                <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Historique des bons</h2>
                {clientDetail.vouchers?.length > 0 ? (
                  <div className="space-y-1">
                    {clientDetail.vouchers.map(v => (
                      <div key={v.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                        <div className="flex items-center gap-2">
                          <span>{v.prize_emoji}</span>
                          <span className="text-[rgb(var(--lm-text-secondary))]">{v.prize_label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[rgb(var(--lm-text-muted))] text-[10px]">{v.code}</span>
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{v.created_at?.slice(0, 10)}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                            v.used ? 'bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))]' :
                            'bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))]'
                          }`}>
                            {v.used ? 'Utilise' : 'Actif'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-xs text-[rgb(var(--lm-text-muted))] py-3">Aucun bon</p>
                )}
              </div>

              <div className="lm-card p-4">
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1 text-xs text-red-400/60 hover:text-red-400 transition-colors"
                    data-testid="delete-client-btn">
                    <Trash2 size={12} /> Supprimer ce client
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-red-400">Supprimer {clientDetail.prenom} {clientDetail.nom} et tous ses bons ?</p>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmDelete(false)} className="text-xs text-[rgb(var(--lm-text-muted))]" data-testid="cancel-delete">Annuler</button>
                      <Button size="sm" onClick={deleteClient}
                        className="bg-red-500 text-white h-7 text-xs hover:bg-red-600" data-testid="confirm-delete">
                        Confirmer la suppression
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                    className="pl-9 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" data-testid="search-clients" />
                </div>
                <a href={`${API}/roue/clients/export-csv`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] h-9" data-testid="export-clients">
                    <Download size={14} className="mr-1" /> CSV
                  </Button>
                </a>
              </div>
              <div className="text-[10px] text-[rgb(var(--lm-text-muted))]">{clients.length} client(s)</div>
              <div className="space-y-1">
                {clients.map(c => (
                  <button key={c.id} onClick={() => openClient(c)}
                    className="w-full lm-card px-4 py-3 text-left hover:border-[rgb(var(--lm-gold)/0.2)] transition-colors" data-testid={`client-row-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{c.prenom} {c.nom}</p>
                        <p className="text-xs text-[rgb(var(--lm-text-muted))]">{c.email} {c.telephone ? `| ${c.telephone}` : ''}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-mono text-[rgb(var(--lm-gold))]">{c.nb_vouchers} bons</span>
                        <span className="text-[10px] text-[rgb(var(--lm-green))] ml-1">({c.nb_used} util.)</span>
                      </div>
                    </div>
                  </button>
                ))}
                {clients.length === 0 && <p className="text-center text-sm text-[rgb(var(--lm-text-muted))] py-8">Aucun client</p>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ TAB: REGLAGES ═══ */}
      {tab === 'reglages' && (
        <div className="lm-card p-4 space-y-4">
          <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Parametres</h2>
          <SettingRow label="Jeu actif" desc="Desactiver empeche les clients de jouer"
            control={<Switch checked={settings.game_active !== false} onCheckedChange={v => saveSettings({ ...settings, game_active: v })} data-testid="toggle-game-active" />} />
          <SettingRow label="QR code sur la carte" desc="Affiche le QR code sur le menu imprime"
            control={<Switch checked={settings.qr_on_menu === true} onCheckedChange={v => saveSettings({ ...settings, qr_on_menu: v })} data-testid="toggle-qr-menu" />} />
          <SettingRow label="Validite des bons (jours)" desc="Duree avant expiration"
            control={<Input type="number" value={settings.validity_days || 30} onChange={e => saveSettings({ ...settings, validity_days: parseInt(e.target.value) || 30 })}
              className="w-20 h-8 text-sm font-mono text-center bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="validity-days" />} />
        </div>
      )}
    </div>
  );
};

/* ── Sub-components ── */
const MiniStat = ({ label, value, icon: Icon }) => (
  <div className="lm-card p-3 flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-gold)/0.1)] flex items-center justify-center shrink-0">
      <Icon size={14} className="text-[rgb(var(--lm-gold))]" />
    </div>
    <div>
      <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">{label}</p>
      <p className="text-sm font-mono font-bold text-[rgb(var(--lm-text))]">{value}</p>
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className="lm-card p-4">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={13} className="text-[rgb(var(--lm-text-muted))]" />
      <span className="text-[9px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">{label}</span>
    </div>
    <p className={`font-mono text-xl font-bold ${color === 'gold' ? 'text-[rgb(var(--lm-gold))]' : color === 'green' ? 'text-[rgb(var(--lm-green))]' : color === 'orange' ? 'text-[rgb(var(--lm-orange))]' : 'text-[rgb(var(--lm-text))]'}`} data-testid={testId}>{value}</p>
  </div>
);

const SettingRow = ({ label, desc, control }) => (
  <div className="flex items-center justify-between py-1 border-b border-[rgb(var(--lm-border)/0.5)] last:border-0">
    <div>
      <p className="text-sm text-[rgb(var(--lm-text))]">{label}</p>
      <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{desc}</p>
    </div>
    {control}
  </div>
);

/* ── Game Previews ── */
const PreviewRoue = () => (
  <div className="text-center">
    <div className="w-20 h-20 rounded-full border-4 border-[rgb(var(--lm-gold))] mx-auto flex items-center justify-center relative"
      style={{ background: 'conic-gradient(#C9A84C 0% 25%, #162238 25% 38%, #009893 38% 56%, #C0604A 56% 71%, #1a2d47 71% 85%, #8B6914 85% 93%, #2D7B7B 93% 100%)' }}>
      <div className="w-6 h-6 rounded-full bg-[rgb(var(--lm-bg))] border-2 border-[rgb(var(--lm-gold))] flex items-center justify-center">
        <span className="text-[6px] font-bold text-[rgb(var(--lm-gold))]">GO</span>
      </div>
    </div>
    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2">Roue de la fortune</p>
  </div>
);

const PreviewGrattage = () => (
  <div className="text-center">
    <div className="w-24 h-16 rounded-lg mx-auto flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #C9A84C 0%, #8B6914 100%)' }}>
      <div className="absolute inset-0 opacity-30" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.1) 4px, rgba(255,255,255,0.1) 8px)' }} />
      <Sparkles size={20} className="text-white/80" />
    </div>
    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2">Carte a gratter</p>
  </div>
);

const PreviewCoffre = () => (
  <div className="text-center">
    <div className="flex gap-2 justify-center">
      {[0, 1, 2].map(i => (
        <div key={i} className="w-12 h-14 rounded-lg flex items-center justify-center" style={{ background: i === 1 ? '#C9A84C20' : '#16223850', border: `1.5px solid ${i === 1 ? '#C9A84C' : '#C9A84C30'}` }}>
          <Box size={16} style={{ color: i === 1 ? '#C9A84C' : '#C9A84C60' }} />
        </div>
      ))}
    </div>
    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2">Coffre mystere</p>
  </div>
);

export default RoueGestion;
